/**
 * The decisions behind the two printed documents (PHASE-0.md §2.6).
 *
 * Pure functions, separate from the components, for the reason `patientDisplay.ts`
 * gives — this is where the safety-critical choices are, and those are worth testing
 * without a DOM. On paper there are three of them: **which identifier the wristband
 * encodes**, **what a dose that cannot be rendered does to the sheet**, and **what a
 * printed page says about where it came from**.
 *
 * **Paper is different from a screen in one way that changes the answers.** A screen is
 * refetched; a sheet is signed against, carried between rooms and read tomorrow. So a
 * value the screen may quietly mark *Kan inte visas* has to be louder here, and a page
 * with no provenance on it is a page nobody can date. Everything else is deliberately
 * the same code as the screen — `safetyPanel`, `identifierLabel` and the shared dose and
 * date formatters are imported, not reimplemented, because the one failure that must be
 * impossible is paper and screen disagreeing about an allergy or a dose.
 *
 * **`t` is a parameter, not an import** (I18N.md §1). A module-level `t()` resolves once,
 * when the bundle is first evaluated, and freezes whatever language was selected then.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import { formatDose } from '../format/clinicalDose'
import type {
  PatientIdentifierView,
  PatientMedication,
  PatientOverview,
} from '../patient/patientOverviewApi'
import { identifierLabel } from '../patient/patientOverviewDisplay'
import type { IdentifierSystem } from '../patient/patientRecord'
import { code128PathData, encodeCode128 } from './code128'

/**
 * The lookup the print screens need.
 *
 * `common` first, so this `t` is still assignable to `Translate` and
 * `PatientOverviewTranslate` — which is what lets the printed safety block come from
 * `safetyPanel` rather than from a second copy of it.
 */
export type PrintTranslate = TFunction<['common', 'patient', 'print', 'death']>

/**
 * One module's width on paper, in millimetres — the barcode's X dimension.
 *
 * The one measurement in this file that a scanner cares about. Code 128's nominal X is
 * 0.25 mm and 0.19 mm is the specified floor; 0.4 mm is comfortably above both, which
 * matters because a consumer laser printer on ordinary paper does not place a 0.25 mm
 * bar accurately and a wristband is read through a curved plastic sleeve.
 *
 * Fixed per module rather than derived from an available width, deliberately: sizing the
 * symbol to a box would make the X dimension depend on how long the identifier is, so a
 * reservnummer would print finer bars than a personnummer and the failure would appear
 * on two patients out of thirty. Here a longer identifier makes a longer barcode, which
 * is what the symbology intends.
 */
export const MODULE_MM = 0.4

/**
 * Bar height, in modules — so the same constant scales with {@link MODULE_MM}.
 *
 * 40 modules is 16 mm, above the 6.35 mm floor and roughly the 15 %-of-length guidance
 * for a twelve-digit payload. Height is not encoded: it exists so that a scanner sweeping
 * at an angle still crosses every bar.
 */
export const BARCODE_HEIGHT_MODULES = 40

/**
 * How many blank signing boxes each medication order gets on the signeringslista.
 *
 * Six, which is a day's worth for the most frequent order in the demo ward's fixture with
 * room left over.
 *
 * **They are blank, and carry no pre-printed times.** A real medication administration
 * record often prints a time grid; this one must not. The frequency is the prescriber's
 * words (`1 gång per dygn`, `var 8:e timme`) and is never parsed anywhere in this system,
 * so turning it into clock times would be a program deciding when a drug is given — the
 * line `AGENTS.md` §3 rule 5 draws. Phase 0 records nothing about administration at all
 * (`ROADMAP.md` puts that in phase 2); the printed sheet with a pen is the compensating
 * control `ARCHITECTURE.md` §12 names, so the nurse writes the time and initials exactly
 * as they do on the sheet this replaces.
 */
export const SIGNING_BOXES = 6

/**
 * Which identifier goes on the wristband, or null when the record holds none.
 *
 * **Precedence is decided here rather than taken from the array's order.** The API sends
 * identifiers "most nationally recognised first", and this returns the same one today —
 * but a wristband is what a scanner reads to confirm identity before a drug is given, and
 * that choice must not be able to change because a query's `ORDER BY` was rewritten. A
 * patient who arrived unidentified and later acquired a personnummer holds both, and the
 * band has to carry the national number.
 *
 * Every identifier still appears as text on the band. Only one can be the barcode.
 */
export function wristbandIdentifier(
  overview: PatientOverview,
): PatientIdentifierView | null {
  const precedence: readonly IdentifierSystem[] = [
    'personnummer',
    'samordningsnummer',
    'reservnummer',
    'unknown-series',
  ]

  for (const system of precedence) {
    const found = overview.identifiers.find((identifier) => identifier.system === system)
    if (found !== undefined) {
      return found
    }
  }

  return null
}

/** A barcode ready to place on the band, or the reason there is none. */
export type WristbandBarcode =
  | {
      readonly kind: 'encoded'

      /**
       * The SVG `viewBox` width, in modules, including both quiet zones.
       *
       * The quiet zones are inside the `viewBox` rather than left to page margins, so
       * scaling the symbol scales the white space with the bars and no margin or
       * table-padding change can take it away. A barcode butted against a rule reads
       * short or not at all.
       */
      readonly modules: number

      /** `d` for a single path, in module units. */
      readonly path: string

      /** Width on paper, in millimetres. */
      readonly widthMm: number

      /** Height on paper, in millimetres. */
      readonly heightMm: number
    }
  | {
      readonly kind: 'unencodable'

      /** What could not be encoded. Developer-facing; the band shows a catalogue string. */
      readonly reason: string
    }

/**
 * Encodes an identifier for the band, or reports that it cannot be encoded.
 *
 * **Never throws, and never renders an approximation.** Those are the two halves of the
 * same rule. A wristband with no barcode is one a nurse reads with their eyes, which is
 * the situation before this system existed; a barcode that decodes to a different
 * identifier is a misidentification with a machine's authority behind it. So an
 * unencodable value becomes a stated absence on the printed band and the identifier is
 * still printed as text — see `Wristband.tsx`, and `code128.ts`'s header for the posture.
 */
export function wristbandBarcode(value: string): WristbandBarcode {
  try {
    const symbology = encodeCode128(value)

    return {
      kind: 'encoded',
      modules: symbology.modules,
      path: code128PathData(symbology, BARCODE_HEIGHT_MODULES),
      widthMm: symbology.modules * MODULE_MM,
      heightMm: BARCODE_HEIGHT_MODULES * MODULE_MM,
    }
  } catch (cause) {
    return {
      kind: 'unencodable',
      reason: cause instanceof Error ? cause.message : String(cause),
    }
  }
}

/**
 * The dose through the single shared formatter, or null when it cannot be printed.
 *
 * **Deliberately not `medicationDose` from `patientOverviewDisplay.ts`**, although it
 * wraps the same `formatDose`. That one is `guarded`: it renders *Kan inte visas* in the
 * cell, which is right on a screen where letting the throw propagate would take the
 * allergy panel down with it. On a sheet a nurse signs against, a quiet phrase in one
 * cell of a table is not enough — the sheet itself has to say, at the top, that it is
 * incomplete. So this reports the failure instead of rendering it, and
 * `AdministrationRecord.tsx` does both: marks the cell and warns above the table.
 *
 * Returning null rather than throwing is what makes the count above the table possible
 * without running the formatter twice in two different ways.
 */
export function printableDose(medication: PatientMedication): string | null {
  try {
    return formatDose(medication.doseAmount, medication.doseUnit)
  } catch {
    return null
  }
}

/** The orders whose dose cannot be printed. Empty in every case that should ever print. */
export function unprintableDoses(
  overview: PatientOverview,
): readonly PatientMedication[] {
  return overview.medications.filter((medication) => printableDose(medication) === null)
}

/**
 * When the sheet was printed — or a statement that the time could not be given.
 *
 * The third decision this file's header names: what a printed page says about where it came
 * from. A page with no provenance on it is a page nobody can date, and dating it is what
 * lets two sheets for the same patient be put in order — which is the whole of what a
 * signeringslista is for once there are two of them.
 *
 * **Deliberately not `guarded`.** That renders the shared *Kan inte visas*, which is right
 * in a cell: it names one value the reader can then get from somewhere else. In the footer
 * the value that cannot be shown is the sheet's **own date**, so *Kan inte visas* leaves a
 * reader to work out which date is missing and to assume the sheet is datable. Saying the
 * time of printing could not be stated tells them the thing they actually need before
 * filing it beside another sheet: this one cannot be placed in a sequence.
 *
 * It can be reached. `formatClinicalDateTime` throws rather than rendering something that
 * could be misread, and the clock behind `now` is load-bearing and local — a box that boots
 * without a network and without its RTC battery has a wrong one
 * (`ARCHITECTURE.md` §"Tidskälla"). A sheet that is honest about being undated is the
 * correct output in that state; a sheet with a plausible wrong date is not.
 */
export function printedAtLine(now: Date, t: PrintTranslate): string {
  try {
    return t('print:document.printedAt', { time: formatClinicalDateTime(now) })
  } catch {
    return t('print:document.printedAtUnknown')
  }
}

/** One identifier as the printed header lists it: a label and the value, verbatim. */
export interface PrintIdentifierLine {
  readonly key: string
  readonly label: string

  /** The identifier exactly as recorded, with its assigner where it has one. */
  readonly value: string
}

/**
 * One identifier, labelled and with its assigner where it has one.
 *
 * **Never reformatted** — a hyphen added or removed makes a visual comparison against a
 * band fail for a number that matches, and the assigner is part of what the value means: a
 * reservnummer is only unique within the unit that issued it, so a block owner printed
 * beside the number is what lets two `TST…` numbers from two units be told apart.
 */
export function identifierLine(
  identifier: PatientIdentifierView,
  t: PrintTranslate,
): PrintIdentifierLine {
  return {
    key: `${identifier.system}-${identifier.value}`,
    label: identifierLabel(identifier.system, t),
    value:
      identifier.assigner === null
        ? identifier.value
        : t('patient:identifier.withAssigner', {
            value: identifier.value,
            assigner: identifier.assigner,
          }),
  }
}

/**
 * Every identifier the patient holds, labelled.
 *
 * All of them, like the overview screen and for the same reason: the reservnummer is what
 * yesterday's notes and the bed sign say, and a printed sheet that carries only the
 * personnummer cannot be matched against them.
 */
export function identifierLines(
  overview: PatientOverview,
  t: PrintTranslate,
): readonly PrintIdentifierLine[] {
  return overview.identifiers.map((identifier) => identifierLine(identifier, t))
}
